import { useState, useEffect } from 'react'
import { useMiniKit } from '@coinbase/onchainkit/minikit'
import Home from './pages/Home'
import Onboarding from './pages/Onboarding'
import Main from './pages/Main'
import Badges from './pages/Badges'
import Vibes from './pages/Vibes'
import Leaderboard from './pages/Leaderboard'
import Settings from './pages/Settings'
import Terms from './pages/Terms'
import Privacy from './pages/Privacy'
import BottomNav from './components/navigation/BottomNav'

const ONBOARDING_COMPLETE_KEY = 'basevibe_onboarding_completed'

type Page = 'vote' | 'badges' | 'vibes' | 'leaderboard' | 'settings' | 'terms' | 'privacy'

function App() {
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(() => {
    // Check localStorage to see if user has completed onboarding before
    return localStorage.getItem(ONBOARDING_COMPLETE_KEY) === 'true'
  })
  const [activePage, setActivePage] = useState<Page>('vote')
  const { setMiniAppReady } = useMiniKit()

  // Notify Farcaster that app is ready
  useEffect(() => {
    // OnchainKit will handle checking if we're in Farcaster context
    setMiniAppReady()
      .then(() => console.log('✅ MiniKit ready via OnchainKit'))
      .catch(() => {
        // Not in Farcaster context - this is fine
        console.log('ℹ️ Not in MiniKit context')
      })
  }, [setMiniAppReady])

  const handleCompleteOnboarding = () => {
    // Persist to localStorage so it doesn't show again
    localStorage.setItem(ONBOARDING_COMPLETE_KEY, 'true')
    setHasCompletedOnboarding(true)
  }

  const handleNavigate = (page: 'terms' | 'privacy') => {
    setActivePage(page)
  }

  const handleBackToSettings = () => {
    setActivePage('settings')
  }

  if (!hasCompletedOnboarding) {
    return <Onboarding onComplete={handleCompleteOnboarding} />
  }

  // Show back button for legal pages
  const showBackButton = activePage === 'terms' || activePage === 'privacy'
  const activeTab = activePage === 'terms' || activePage === 'privacy' ? 'settings' : activePage

  return (
    <div className="min-h-screen text-white">
      {showBackButton ? (
        <header className="sticky top-0 z-50 w-full border-b border-glass-border bg-black/20 backdrop-blur-md">
          <div className="container mx-auto flex h-14 items-center px-4">
            <button
              onClick={handleBackToSettings}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-sm font-medium">Back</span>
            </button>
          </div>
        </header>
      ) : (
        <Home />
      )}

      {/* Content Area */}
      <main className="pb-24 pt-4 px-4 max-w-md mx-auto">
        {activePage === 'vote' && <Main />}
        {activePage === 'badges' && <Badges />}
        {activePage === 'vibes' && <Vibes />}
        {activePage === 'leaderboard' && <Leaderboard />}
        {activePage === 'settings' && <Settings onNavigate={handleNavigate} />}
        {activePage === 'terms' && <Terms />}
        {activePage === 'privacy' && <Privacy />}
      </main>

      {/* Bottom Navigation */}
      <BottomNav activeTab={activeTab as 'vote' | 'badges' | 'vibes' | 'leaderboard' | 'settings'} onTabChange={(tab) => setActivePage(tab)} />
    </div>
  )
}

export default App

